import React from "react";
import SectionCrudPage from "./SectionCrudPage";
import {
  CrmCheckboxInput,
  CrmColorInput,
  CrmDateInput,
  CrmDateTimeInput,
  CrmFormGrid,
  CrmNumberInput,
  CrmSelectInput,
  CrmTextInput,
} from "../../components/crm/CrudFormControls";

const asText = (value) => String(value ?? "").trim();
const asNullableText = (value) => {
  const next = asText(value);
  return next === "" ? null : next;
};
const asNullable = (value) => (value === "" || value === undefined ? null : value);
const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};
const asNullableNumber = (value) => {
  if (value === "" || value === undefined || value === null) return null;
  return asNumber(value);
};
const asDateTime = (value) => {
  const next = asText(value);
  if (next === "") return "";
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(next)) return `${next.replace("T", " ")}:00`;
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(next)) return next.replace("T", " ");
  return next;
};
const asNullableDateTime = (value) => {
  const next = asText(value);
  if (next === "") return null;
  return asDateTime(next);
};

const selectSource = (name, endpoint, labelFields, valueField = "id") => ({
  name,
  optionSource: { endpoint, valueField, labelFields },
});

const WEEKDAY_OPTIONS = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

function GymClassesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
          { value: "archived", label: "Archived" },
        ]}
      />
      <CrmTextInput
        name="code"
        label="Code"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="name"
        label="Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="modality_id"
        label="Modality"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.modality_id || []}
        nullable
      />
      <CrmSelectInput
        name="room_id"
        label="Room"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.room_id || []}
        nullable
      />
      <CrmNumberInput
        name="class_priority"
        label="Class Priority"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmColorInput
        name="color"
        label="Color"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="professor_id"
        label="Professor"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.professor_id || []}
        nullable
      />
      <CrmSelectInput
        name="intensity_id"
        label="Intensity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.intensity_id || []}
        nullable
      />
      <CrmSelectInput
        name="complexity_id"
        label="Complexity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.complexity_id || []}
        nullable
      />
      <CrmSelectInput
        name="level_id"
        label="Level"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.level_id || []}
        nullable
      />
      <CrmSelectInput
        name="physical_condition_id"
        label="Physical Condition Required"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.physical_condition_id || []}
        nullable
      />
      <CrmTextInput
        name="description"
        label="Description"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="capacity"
        label="Capacity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="overbooking_allowed"
        label="Overbooking Allowed"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="occupancy_target"
        label="Occupancy Target"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmCheckboxInput
        name="attendance_control_enabled"
        label="Attendance Control Enabled"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="attendance_control_from"
        label="Attendance Control From (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="fixed_coefficient"
        label="Fixed Coefficient"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="analysis_group_id"
        label="Analysis Group"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.analysis_group_id || []}
        nullable
      />
      <CrmNumberInput
        name="cost_responsible"
        label="Cost Responsible"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="cost_license"
        label="Cost License"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="cost_other"
        label="Cost Other"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="cost_notes"
        label="Cost Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function ClassSessionsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="class_id"
        label="Gym Class"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.class_id || []}
      />
      <CrmDateTimeInput
        name="starts_at"
        label="Starts At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="ends_at"
        label="Ends At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="capacity"
        label="Capacity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="schedule_id"
        label="Schedule"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.schedule_id || []}
        nullable
      />
      <CrmSelectInput
        name="location_zone_id"
        label="Zone"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.location_zone_id || []}
        nullable
      />
      <CrmSelectInput
        name="room_id"
        label="Room"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.room_id || []}
        nullable
      />
      <CrmSelectInput
        name="professor_id"
        label="Professor"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.professor_id || []}
        nullable
      />
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "scheduled", label: "Scheduled" },
          { value: "cancelled", label: "Cancelled" },
          { value: "completed", label: "Completed" },
        ]}
      />
    </CrmFormGrid>
  );
}

function ClassBookingsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="class_session_id"
        label="Class Session"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.class_session_id || []}
      />
      <CrmSelectInput
        name="user_id"
        label="User"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.user_id || []}
      />
      <CrmSelectInput
        name="entitlement_id"
        label="Entitlement"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.entitlement_id || []}
        nullable
      />
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "booked", label: "Booked" },
          { value: "checked_in", label: "Checked In" },
          { value: "cancelled", label: "Cancelled" },
          { value: "no_show", label: "No Show" },
        ]}
      />
      <CrmDateTimeInput
        name="booked_at"
        label="Booked At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="cancelled_at"
        label="Cancelled At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateTimeInput
        name="checked_in_at"
        label="Checked In At (YYYY-MM-DD HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="notes"
        label="Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function ClassPackAllowedClassesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="class_pack_product_id"
        label="Class Pack (Product ID)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.class_pack_product_id || []}
      />
      <CrmSelectInput
        name="class_id"
        label="Gym Class"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.class_id || []}
      />
    </CrmFormGrid>
  );
}

function ClassSchedulesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="class_id"
        label="Gym Class"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.class_id || []}
      />
      <CrmSelectInput
        name="status"
        label="Status"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "active", label: "Active" },
          { value: "inactive", label: "Inactive" },
        ]}
      />
      <CrmSelectInput
        name="weekday"
        label="Weekday"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={WEEKDAY_OPTIONS}
      />
      <CrmTextInput
        name="start_time"
        label="Start Time (HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="end_time"
        label="End Time (HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateInput
        name="start_date"
        label="Start Date"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmDateInput
        name="end_date"
        label="End Date"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="room_id"
        label="Room"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.room_id || []}
        nullable
      />
      <CrmSelectInput
        name="professor_id"
        label="Professor"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.professor_id || []}
        nullable
      />
      <CrmNumberInput
        name="capacity_override"
        label="Capacity Override"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function ClassScheduleExceptionsForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="schedule_id"
        label="Class Schedule"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.schedule_id || []}
      />
      <CrmDateInput
        name="date"
        label="Date"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmCheckboxInput
        name="is_cancelled"
        label="Cancelled"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="override_start_time"
        label="Override Start Time (HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="override_end_time"
        label="Override End Time (HH:MM:SS)"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmSelectInput
        name="override_room_id"
        label="Override Room"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.override_room_id || []}
        nullable
      />
      <CrmSelectInput
        name="override_professor_id"
        label="Override Professor"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.override_professor_id || []}
        nullable
      />
      <CrmTextInput
        name="notes"
        label="Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

function ClassRequiredResourcesForm({ formData, setFormData, errors, clearFieldError, selectOptions }) {
  return (
    <CrmFormGrid>
      <CrmSelectInput
        name="class_id"
        label="Gym Class"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={selectOptions.class_id || []}
      />
      <CrmSelectInput
        name="resource_type"
        label="Resource Type"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
        options={[
          { value: "equipment", label: "Equipment" },
          { value: "personnel", label: "Personnel" },
        ]}
      />
      <CrmTextInput
        name="resource_name"
        label="Resource Name"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmNumberInput
        name="quantity"
        label="Quantity"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
      <CrmTextInput
        name="notes"
        label="Notes"
        formData={formData}
        setFormData={setFormData}
        errors={errors}
        clearFieldError={clearFieldError}
      />
    </CrmFormGrid>
  );
}

const classesResources = [
  {
    key: "gym-classes",
    label: "Gym Classes",
    endpoint: "/gym-classes",
    createInitialFormData: () => ({
      status: "active",
      class_priority: 0,
      overbooking_allowed: 0,
      attendance_control_enabled: false,
    }),
    tableFields: [
      { name: "name", label: "Name" },
      { name: "status", label: "Status" },
      { name: "class_priority", label: "Priority" },
      {
        name: "modality_id",
        label: "Modality",
        valueGetter: (row) => row?.modality_name || row?.modality?.name || row?.modality_id,
      },
      {
        name: "room_id",
        label: "Room",
        valueGetter: (row) => row?.room_name || row?.room?.name || row?.room_id,
      },
      {
        name: "professor_id",
        label: "Professor",
        valueGetter: (row) => row?.professor_name || row?.professor?.name || row?.professor_id,
      },
      { name: "capacity", label: "Capacity" },
      { name: "overbooking_allowed", label: "Overbooking" },
      { name: "attendance_control_enabled", label: "Attendance Control" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      {
        key: "class-sessions",
        label: "Class Sessions",
        endpoint: "/class-sessions",
        foreignKey: "class_id",
      },
      {
        key: "class-schedules",
        label: "Schedules",
        endpoint: "/class-schedules",
        foreignKey: "class_id",
      },
      {
        key: "class-required-resources",
        label: "Required Resources",
        endpoint: "/class-required-resources",
        foreignKey: "class_id",
      },
      {
        key: "class-pack-allowed-classes",
        label: "Allowed In Class Packs",
        endpoint: "/class-pack-allowed-classes",
        foreignKey: "class_id",
      },
    ],
    selectSources: [
      selectSource("modality_id", "/class-modalities", ["name", "code"]),
      selectSource("room_id", "/rooms", ["name", "code"]),
      selectSource("professor_id", "/user/employees-lookup", ["name"]),
      selectSource("intensity_id", "/class-intensities", ["name", "code"]),
      selectSource("complexity_id", "/class-complexities", ["name", "code"]),
      selectSource("level_id", "/class-levels", ["name", "code"]),
      selectSource("physical_condition_id", "/class-physical-conditions", ["name", "code"]),
      selectSource("analysis_group_id", "/class-analysis-groups", ["name", "code"]),
    ],
    renderForm: GymClassesForm,
    toPayload: (formData) => ({
      status: asText(formData.status),
      code: asNullableText(formData.code),
      name: asText(formData.name),
      modality_id: asNullable(formData.modality_id),
      room_id: asNullable(formData.room_id),
      class_priority: asNumber(formData.class_priority ?? 0),
      color: asNullableText(formData.color),
      professor_id: asNullable(formData.professor_id),
      intensity_id: asNullable(formData.intensity_id),
      complexity_id: asNullable(formData.complexity_id),
      level_id: asNullable(formData.level_id),
      physical_condition_id: asNullable(formData.physical_condition_id),
      description: asNullableText(formData.description),
      default_duration_minutes: asNullableNumber(formData.default_duration_minutes),
      default_capacity: asNullableNumber(formData.default_capacity),
      capacity: asNullableNumber(formData.capacity),
      overbooking_allowed: asNumber(formData.overbooking_allowed ?? 0),
      occupancy_target: asNullableNumber(formData.occupancy_target),
      attendance_control_enabled: Boolean(formData.attendance_control_enabled),
      attendance_control_from: asNullableDateTime(formData.attendance_control_from),
      fixed_coefficient: asNullableNumber(formData.fixed_coefficient),
      analysis_group_id: asNullable(formData.analysis_group_id),
      cost_responsible: asNullableNumber(formData.cost_responsible),
      cost_license: asNullableNumber(formData.cost_license),
      cost_other: asNullableNumber(formData.cost_other),
      cost_notes: asNullableText(formData.cost_notes),
    }),
  },
  {
    key: "class-sessions",
    label: "Class Sessions",
    endpoint: "/class-sessions",
    tableFields: [
      {
        name: "class_id",
        label: "Gym Class",
        valueGetter: (row) => row?.class_name || row?.gym_class?.name || row?.class_id,
      },
      { name: "starts_at", label: "Starts At" },
      { name: "ends_at", label: "Ends At" },
      { name: "capacity", label: "Capacity" },
      {
        name: "schedule_id",
        label: "Schedule",
        valueGetter: (row) => row?.schedule?.id || row?.schedule_id,
      },
      {
        name: "location_zone_id",
        label: "Zone",
        valueGetter: (row) => row?.zone_name || row?.location_zone?.name || row?.location_zone_id,
      },
      {
        name: "room_id",
        label: "Room",
        valueGetter: (row) => row?.room_name || row?.room?.name || row?.room_id,
      },
      {
        name: "professor_id",
        label: "Professor",
        valueGetter: (row) => row?.professor_name || row?.professor?.name || row?.professor_id,
      },
      { name: "status", label: "Status" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    relatedTabs: [
      {
        key: "class-bookings",
        label: "Bookings",
        endpoint: "/class-bookings",
        foreignKey: "class_session_id",
      },
    ],
    selectSources: [
      selectSource("class_id", "/gym-classes", ["name", "code", "uuid"]),
      selectSource("schedule_id", "/class-schedules", ["weekday", "start_time", "end_time"]),
      selectSource("location_zone_id", "/zones", ["name", "code", "uuid"]),
      selectSource("room_id", "/rooms", ["name", "code"]),
      selectSource("professor_id", "/user/employees-lookup", ["name"]),
    ],
    renderForm: ClassSessionsForm,
    toPayload: (formData) => ({
      class_id: asNullable(formData.class_id),
      schedule_id: asNullable(formData.schedule_id),
      starts_at: asDateTime(formData.starts_at),
      ends_at: asDateTime(formData.ends_at),
      capacity: asNullableNumber(formData.capacity),
      location_zone_id: asNullable(formData.location_zone_id),
      room_id: asNullable(formData.room_id),
      professor_id: asNullable(formData.professor_id),
      status: asText(formData.status),
    }),
  },
  {
    key: "class-schedules",
    label: "Class Schedules",
    endpoint: "/class-schedules",
    tableFields: [
      {
        name: "class_id",
        label: "Gym Class",
        valueGetter: (row) => row?.gym_class?.name || row?.class_id,
      },
      {
        name: "weekday",
        label: "Weekday",
        valueGetter: (row) => WEEKDAY_OPTIONS.find((option) => Number(option.value) === Number(row?.weekday))?.label || row?.weekday,
      },
      { name: "start_time", label: "Start Time" },
      { name: "end_time", label: "End Time" },
      { name: "start_date", label: "Start Date" },
      { name: "end_date", label: "End Date" },
      {
        name: "room_id",
        label: "Room",
        valueGetter: (row) => row?.room?.name || row?.room_id,
      },
      {
        name: "professor_id",
        label: "Professor",
        valueGetter: (row) => row?.professor?.name || row?.professor_id,
      },
      { name: "capacity_override", label: "Capacity Override" },
      { name: "status", label: "Status" },
    ],
    relatedTabs: [
      {
        key: "class-schedule-exceptions",
        label: "Exceptions",
        endpoint: "/class-schedule-exceptions",
        foreignKey: "schedule_id",
      },
    ],
    selectSources: [
      selectSource("class_id", "/gym-classes", ["name", "code"]),
      selectSource("room_id", "/rooms", ["name", "code"]),
      selectSource("professor_id", "/user/employees-lookup", ["name"]),
    ],
    renderForm: ClassSchedulesForm,
    toPayload: (formData) => ({
      class_id: asNullable(formData.class_id),
      status: asText(formData.status),
      weekday: asNumber(formData.weekday),
      start_time: asText(formData.start_time),
      end_time: asText(formData.end_time),
      start_date: asNullableText(formData.start_date),
      end_date: asNullableText(formData.end_date),
      room_id: asNullable(formData.room_id),
      professor_id: asNullable(formData.professor_id),
      capacity_override: asNullableNumber(formData.capacity_override),
    }),
  },
  {
    key: "class-schedule-exceptions",
    label: "Class Schedule Exceptions",
    endpoint: "/class-schedule-exceptions",
    tableFields: [
      {
        name: "schedule_id",
        label: "Schedule",
        valueGetter: (row) => row?.schedule?.id || row?.schedule_id,
      },
      { name: "date", label: "Date" },
      { name: "is_cancelled", label: "Cancelled" },
      { name: "override_start_time", label: "Override Start" },
      { name: "override_end_time", label: "Override End" },
      {
        name: "override_room_id",
        label: "Override Room",
        valueGetter: (row) => row?.override_room?.name || row?.override_room_id,
      },
      {
        name: "override_professor_id",
        label: "Override Professor",
        valueGetter: (row) => row?.override_professor?.name || row?.override_professor_id,
      },
      { name: "notes", label: "Notes" },
    ],
    selectSources: [
      selectSource("schedule_id", "/class-schedules", ["weekday", "start_time", "end_time"]),
      selectSource("override_room_id", "/rooms", ["name", "code"]),
      selectSource("override_professor_id", "/user/employees-lookup", ["name"]),
    ],
    renderForm: ClassScheduleExceptionsForm,
    toPayload: (formData) => ({
      schedule_id: asNullable(formData.schedule_id),
      date: asText(formData.date),
      is_cancelled: Boolean(formData.is_cancelled),
      override_start_time: asNullableText(formData.override_start_time),
      override_end_time: asNullableText(formData.override_end_time),
      override_room_id: asNullable(formData.override_room_id),
      override_professor_id: asNullable(formData.override_professor_id),
      notes: asNullableText(formData.notes),
    }),
  },
  {
    key: "class-required-resources",
    label: "Class Required Resources",
    endpoint: "/class-required-resources",
    tableFields: [
      {
        name: "class_id",
        label: "Gym Class",
        valueGetter: (row) => row?.gym_class?.name || row?.class_id,
      },
      { name: "resource_type", label: "Type" },
      { name: "resource_name", label: "Resource" },
      { name: "quantity", label: "Quantity" },
      { name: "notes", label: "Notes" },
    ],
    selectSources: [
      selectSource("class_id", "/gym-classes", ["name", "code"]),
    ],
    renderForm: ClassRequiredResourcesForm,
    toPayload: (formData) => ({
      class_id: asNullable(formData.class_id),
      resource_type: asText(formData.resource_type),
      resource_name: asText(formData.resource_name),
      quantity: asNumber(formData.quantity),
      notes: asNullableText(formData.notes),
    }),
  },
  {
    key: "class-bookings",
    label: "Class Bookings",
    endpoint: "/class-bookings",
    tableFields: [
      {
        name: "class_session_id",
        label: "Class Session",
        valueGetter: (row) => row?.class_session_label || row?.class_session?.class_name || row?.class_session_id,
      },
      {
        name: "user_id",
        label: "User",
        valueGetter: (row) => row?.user_name || row?.user?.name || row?.user?.email || row?.user_id,
      },
      { name: "status", label: "Status" },
      { name: "booked_at", label: "Booked At" },
      { name: "cancelled_at", label: "Cancelled At" },
      { name: "checked_in_at", label: "Checked In At" },
      { name: "notes", label: "Notes" },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("class_session_id", "/class-sessions", ["uuid", "starts_at", "status"]),
      selectSource("user_id", "/users", ["name", "email"]),
      selectSource("entitlement_id", "/member-entitlements", ["uuid", "status"]),
    ],
    renderForm: ClassBookingsForm,
    toPayload: (formData) => ({
      class_session_id: asNullable(formData.class_session_id),
      user_id: asNullable(formData.user_id),
      entitlement_id: asNullable(formData.entitlement_id),
      status: asText(formData.status),
      booked_at: asDateTime(formData.booked_at),
      cancelled_at: asNullableDateTime(formData.cancelled_at),
      checked_in_at: asNullableDateTime(formData.checked_in_at),
      notes: asNullableText(formData.notes),
    }),
  },
];

const hiddenClassesResourceLookup = {
  "class-pack-allowed-classes": {
    key: "class-pack-allowed-classes",
    label: "Class Pack Allowed Classes",
    endpoint: "/class-pack-allowed-classes",
    tableFields: [
      {
        name: "class_pack_product_id",
        label: "Class Pack",
        valueGetter: (row) => row?.class_pack_label || row?.class_pack?.product_name || row?.class_pack_product_id,
      },
      {
        name: "class_id",
        label: "Gym Class",
        valueGetter: (row) => row?.class_name || row?.gym_class?.name || row?.class_id,
      },
      { name: "created_at", label: "Created At" },
      { name: "updated_at", label: "Updated At" },
    ],
    selectSources: [
      selectSource("class_pack_product_id", "/class-packs", ["product_id", "entries", "validity_days"], "product_id"),
      selectSource("class_id", "/gym-classes", ["name", "code", "uuid"]),
    ],
    renderForm: ClassPackAllowedClassesForm,
    toPayload: (formData) => ({
      class_pack_product_id: asNullable(formData.class_pack_product_id),
      class_id: asNullable(formData.class_id),
    }),
  },
};

export default function ClassesPage() {
  const resourceLookup = React.useMemo(() => {
    const own = Object.fromEntries(classesResources.map((resource) => [resource.key, resource]));
    return { ...own, ...hiddenClassesResourceLookup };
  }, []);

  return (
    <SectionCrudPage
      title="Classes"
      subtitle="Manage gym classes, sessions and bookings."
      resources={classesResources}
      resourceLookup={resourceLookup}
    />
  );
}
